// Toolbar button + settings panel for the cloud shadow (Jake, 13 Oct 2026; see cesium/cloudShadow.ts).
// GEAAR changes from the zip: OFF by default on every device (Chad, 13 Oct: "tertiary ... OFF by
// default"), site fonts, and the phone bottom sheet sits above the review bar (z-index).
//
//   import { addCloudShadowControls } from "./cloudShadowUI";
//   addCloudShadowControls(viewer);                       // appends a button to .toolbar
//   addCloudShadowControls(viewer, { mount: someElement }); // or put the button somewhere else
//
// The panel drops down under the button on desktop and becomes a bottom sheet on phones.
// Settings are remembered per browser. The effect is only built the first time it is switched on.

import type { Viewer } from "cesium";
import { createCloudShadow, type CloudShadow } from "../cesium/cloudShadow";

const STORAGE_KEY = "geaar.cloudShadow";

type Key = "strength" | "coverage" | "softness" | "speed";
const SLIDERS: { key: Key; label: string; min: number; max: number; step: number; show: (v: number) => string }[] = [
  // The slider tops out at a strength of 0.4 and calls that 100%: anything darker is too heavy.
  { key: "strength", label: "Intensity", min: 0, max: 0.4, step: 0.004, show: (v: number) => Math.round(v / 0.4 * 100) + "%" },
  { key: "coverage", label: "Amount", min: 0, max: 1, step: 0.01, show: (v: number) => Math.round(v * 100) + "%" },
  { key: "softness", label: "Softness", min: 0.005, max: 0.2, step: 0.005, show: (v: number) => Math.round(v / 0.2 * 100) + "%" },
  // The slider starts at 180 m/s and calls that 0%, so the shadow is always drifting, never parked.
  { key: "speed", label: "Speed", min: 180, max: 300, step: 1.2, show: (v: number) => Math.round((v - 180) / 1.2) + "%" },
];

// Size (3.6 km blobs) and direction are fixed on purpose: they are createCloudShadow's defaults, not sliders.
const DEFAULT_VALUES = { strength: 0.4, coverage: 0.4, softness: 0.07, speed: 180 };

const CSS = `
.gcs-panel{position:fixed;z-index:1650;box-sizing:border-box;width:270px;padding:12px 14px;background:#16191e;border:1px solid #333b44;border-radius:6px;color:#e6e9ec;font:12px var(--font-body);box-shadow:0 8px 24px rgba(0,0,0,.5)}
.gcs-panel[hidden]{display:none}
.gcs-head{display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;font-weight:bold}
.gcs-head label{display:flex;align-items:center;gap:6px;font-weight:normal;cursor:pointer}
.gcs-row{display:grid;grid-template-columns:62px 1fr 46px;align-items:center;gap:8px;min-height:28px}
.gcs-row input{width:100%;margin:0;accent-color:#e5202e}
.gcs-val{text-align:right;color:#9aa4ae;font-variant-numeric:tabular-nums}
.gcs-off .gcs-row{opacity:.4}
.gcs-foot{display:flex;justify-content:flex-end;gap:8px;margin-top:8px}
.tool-btn.gcs-on{border-color:#e5202e;color:#fff}
@media (max-width:640px){
  .gcs-panel{left:0!important;right:0!important;top:auto!important;bottom:0;width:auto;max-height:70vh;overflow-y:auto;border-radius:12px 12px 0 0;border-width:1px 0 0;padding:14px 16px calc(14px + env(safe-area-inset-bottom));font-size:15px}
  .gcs-row{grid-template-columns:78px 1fr 56px;min-height:44px}
  .gcs-row input{height:32px}
  .gcs-head input{width:22px;height:22px}
  .gcs-foot button{min-height:40px;padding:0 16px;font-size:14px}
}`;

export function addCloudShadowControls(viewer: Viewer, { mount = document.querySelector(".toolbar") as HTMLElement | null } = {}) {
  // Phones get fewer idle redraws. Every device starts switched off; a viewer who turns it on has
  // that remembered in this browser only.
  const phone = matchMedia("(pointer: coarse) and (max-width: 820px)").matches;

  let saved: Record<string, unknown> = {};
  try { saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null") ?? {}; } catch { /* private mode, or nothing saved yet */ }
  const state = { on: false, ...DEFAULT_VALUES, ...saved } as { on: boolean } & Record<Key, number>;
  for (const s of SLIDERS) state[s.key] = Math.min(s.max, Math.max(s.min, Number(state[s.key]) || 0)); // saved values from an older range
  const save = () => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* storage unavailable */ } };

  let clouds: CloudShadow | null = null;
  const values = () => {
    const v = {} as Record<Key, number>;
    for (const { key } of SLIDERS) v[key] = state[key];
    return v;
  };
  const apply = () => {
    if (state.on) {
      if (!clouds) clouds = createCloudShadow(viewer, { ...values(), fps: phone ? 12 : 20 });
      else { clouds.set(values()); clouds.enabled = true; }
    } else if (clouds) clouds.enabled = false;
    button.classList.toggle("gcs-on", state.on);
    panel.classList.toggle("gcs-off", !state.on);
    viewer.scene.requestRender();
  };

  if (!document.getElementById("gcs-style")) {
    const style = document.createElement("style");
    style.id = "gcs-style"; style.textContent = CSS;
    document.head.append(style);
  }

  const el = <K extends keyof HTMLElementTagNameMap>(tag: K, props: Partial<HTMLElementTagNameMap[K]> = {}, ...kids: (Node | string)[]): HTMLElementTagNameMap[K] => {
    const e = Object.assign(document.createElement(tag), props); e.append(...kids); return e;
  };

  const button = el("button", { type: "button", className: "tool-btn", textContent: "☁ Shadows", title: "Cloud shadow settings" });
  button.setAttribute("aria-haspopup", "dialog");

  const onBox = el("input", { type: "checkbox", checked: state.on });
  onBox.addEventListener("change", () => { state.on = onBox.checked; save(); apply(); });

  const panel = el("div", { className: "gcs-panel", hidden: true },
    el("div", { className: "gcs-head" }, el("span", { textContent: "Cloud shadows" }), el("label", {}, onBox, "On")));
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-label", "Cloud shadow settings");

  const inputs: Record<string, { input: HTMLInputElement; out: HTMLSpanElement; s: (typeof SLIDERS)[number] }> = {};
  for (const s of SLIDERS) {
    const out = el("span", { className: "gcs-val", textContent: s.show(state[s.key]) });
    const input = el("input", { type: "range", min: String(s.min), max: String(s.max), step: String(s.step), value: String(state[s.key]) });
    input.setAttribute("aria-label", s.label);
    input.addEventListener("input", () => {
      state[s.key] = Number(input.value); out.textContent = s.show(state[s.key]);
      if (clouds && state.on) clouds.set(values());
      viewer.scene.requestRender();
    });
    input.addEventListener("change", save);
    inputs[s.key] = { input, out, s };
    panel.append(el("div", { className: "gcs-row" }, el("span", { textContent: s.label }), input, out));
  }

  const reset = el("button", { type: "button", className: "tool-btn", textContent: "Reset" });
  reset.addEventListener("click", () => {
    Object.assign(state, DEFAULT_VALUES);
    for (const { input, out, s } of Object.values(inputs)) { input.value = String(state[s.key]); out.textContent = s.show(state[s.key]); }
    save(); apply();
  });
  const done = el("button", { type: "button", className: "tool-btn", textContent: "Done" });
  panel.append(el("div", { className: "gcs-foot" }, reset, done));

  const place = () => {
    const r = button.getBoundingClientRect(), w = panel.offsetWidth;
    panel.style.top = r.bottom + 6 + "px";
    panel.style.left = Math.max(8, Math.min(r.right - w, innerWidth - w - 8)) + "px";
  };
  const open = (yes: boolean) => { panel.hidden = !yes; button.setAttribute("aria-expanded", String(yes)); if (yes) place(); };
  button.addEventListener("click", () => open(panel.hidden));
  done.addEventListener("click", () => open(false));
  const onOutside = (e: PointerEvent) => { const t = e.target as Node; if (!panel.hidden && !panel.contains(t) && !button.contains(t)) open(false); };
  const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") open(false); };
  const onResize = () => { if (!panel.hidden) place(); };
  document.addEventListener("pointerdown", onOutside);
  document.addEventListener("keydown", onKey);
  addEventListener("resize", onResize);

  (mount ?? document.body).append(button);
  document.body.append(panel);
  apply();

  return {
    button, panel,
    get clouds() { return clouds; },
    destroy() {
      document.removeEventListener("pointerdown", onOutside);
      document.removeEventListener("keydown", onKey);
      removeEventListener("resize", onResize);
      button.remove(); panel.remove(); clouds?.destroy();
    },
  };
}
